import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PWAService } from './services/pwa/pwa.service';
import { ModalService, ModalOptions, ConfirmationFeedbackService, FeedbackOptions } from './shared/components/index';
import { NotificationService } from './services/api/notification.service';
import { PushNotificationService } from './services/pwa/push-notification.service';
import { AuthService } from './auth/services/auth.service';
import { MobileLifecycleService, MobilePlatformService } from './services/mobile';
import { LocationConsentService } from './services/location/location-consent.service';
// import { ScreenProtectionService } from './services/security/screen-protection.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
    // Propriedades públicas
    modalOptions: ModalOptions | null = null;
    feedbackOptions: FeedbackOptions | null = null;

    // Propriedades privadas
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly titleService = inject(Title);
    private readonly pwaService = inject(PWAService);
    private readonly modalService = inject(ModalService);
    private readonly feedbackService = inject(ConfirmationFeedbackService);
    private readonly notificationService = inject(NotificationService);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly authService = inject(AuthService);
    private readonly mobileLifecycle = inject(MobileLifecycleService);
    private readonly mobilePlatform = inject(MobilePlatformService);
    private readonly locationConsentService = inject(LocationConsentService);
    // protected readonly screenProtectionService = inject(ScreenProtectionService);

    // Subscriptions
    private modalSub!: Subscription;
    private feedbackSub!: Subscription;
    private routerSub!: Subscription;

    // Flags para controle de push notifications
    private pushNotificationInicializada = false;

    // Termo de localização: já verificado nesta sessão?
    private termoLocalizacaoVerificado = false;

    async ngOnInit(): Promise<void> {
        // this.screenProtectionService.initialize();
        await this.mobileLifecycle.initialize();
        this.initializeRouterEvents();
        this.initializeModalSubscription();
        this.initializeFeedbackSubscription();
        this.initializeNotifications();
        this.logPWAStatus();
    }

    ngOnDestroy(): void {
        this.cleanupSubscriptions();
    }

    // Métodos públicos para interação com modal
    onModalConfirm(): void {
        this.modalService.confirm();
    }

    onModalCancel(): void {
        this.modalService.cancel();
    }

    onFeedbackClosed(): void {
        this.feedbackService.hide();
    }

    // Métodos privados para inicialização
    private initializeRouterEvents(): void {
        this.routerSub = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map(route => this.getDeepestChildRoute(route)),
                filter(route => route.outlet === 'primary'),
                switchMap(route => this.extractRouteData(route)),
                tap(data => this.updatePageTitle(data.title))
            )
            .subscribe();
    }

    private initializeModalSubscription(): void {
        this.modalSub = this.modalService.modalState.subscribe(options => {
            this.modalOptions = options;
        });
    }

    private initializeFeedbackSubscription(): void {
        this.feedbackSub = this.feedbackService.feedbackState.subscribe(options => {
            this.feedbackOptions = options;
        });
    }

    // Métodos privados para manipulação de rotas
    private getDeepestChildRoute(route: ActivatedRoute): ActivatedRoute {
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    private extractRouteData(route: ActivatedRoute) {
        return route.data.pipe(
            map((routeData: any) => ({
                title: routeData['title'] || '',
            }))
        );
    }

    private updatePageTitle(title: string): void {
        const baseTitle = 'INFRASEG';
        const fullTitle = title ? `${title}` : baseTitle;
        this.titleService.setTitle(fullTitle);
    }

    // Inicializar sistema de notificações
    private initializeNotifications(): void {
        // O NotificationService vai aguardar o socket conectar internamente
        this.notificationService.ngOnInit();

        // Monitorar mudanças no estado de autenticação
        // Quando usuário fizer login, inicializar push notifications
        this.monitorarAutenticacao();

        // Inicializar push notifications automaticamente se já tiver permissão E estiver autenticado
        this.inicializarPushNotificationsAutomaticamente();

        // Monitorar mudanças na permissão de notificações
        this.monitorarPermissaoNotificacoes();

        // App nativo: pedir permissão push via Capacitor (não usa Notification API do browser)
        if (this.mobilePlatform.isNative()) {
            setTimeout(() => this.inicializarPushNotificationsAutomaticamente(), 1500);
            return;
        }

        // Web/PWA: prompt clássico do browser
        if ('Notification' in window && Notification.permission === 'default') {
            setTimeout(() => {
                this.notificationService
                    .solicitarPermissaoNotificacoes()
                    .then(() => this.inicializarPushNotificationsAutomaticamente())
                    .catch(() => {});
            }, 3000);
        }
    }

    /**
     * Monitora mudanças no estado de autenticação
     * Quando usuário fizer login, tenta inicializar push notifications
     */
    private monitorarAutenticacao(): void {
        // Observar mudanças no estado de autenticação
        // Quando isAuthenticated mudar para true, tentar inicializar push
        setInterval(() => {
            const isAuthenticated = this.authService.authStateSignal().isAuthenticated;
            if (isAuthenticated && !this.pushNotificationInicializada) {
                console.log('[AppComponent] 🔐 Usuário autenticado detectado - tentando inicializar push notifications');
                this.inicializarPushNotificationsAutomaticamente();
            }

            this.verificarTermoLocalizacao(isAuthenticated);
        }, 2000); // Verificar a cada 2 segundos
    }

    /**
     * Termo de uso de localização, verificado uma vez por sessão.
     *
     * Marca a flag ANTES do await: sem isso, a amostra seguinte do monitor (2s)
     * entraria aqui antes da primeira terminar e abriria o modal duas vezes.
     */
    private verificarTermoLocalizacao(isAuthenticated: boolean): void {
        if (isAuthenticated && !this.termoLocalizacaoVerificado) {
            this.termoLocalizacaoVerificado = true;

            this.locationConsentService.verificar().catch((error) => {
                console.error('[AppComponent] Falha ao verificar termo de localização:', error);
            });
            return;
        }

        // Logout limpa o estado: quem entrar depois não pode herdar o aceite do
        // usuário anterior.
        if (!isAuthenticated && this.termoLocalizacaoVerificado) {
            this.termoLocalizacaoVerificado = false;
            this.locationConsentService.limparEstado();
        }
    }

    /**
     * Monitora mudanças na permissão de notificações
     * Se a permissão mudar para 'granted', inicializa push notifications automaticamente
     * IMPORTANTE: Só inicializa se o usuário estiver autenticado
     */
    private monitorarPermissaoNotificacoes(): void {
        if (!('Notification' in window)) {
            return;
        }

        // Verificar permissão periodicamente (a cada 10 segundos)
        // Isso captura mudanças mesmo se o usuário conceder permissão em outra aba
        setInterval(() => {
            // ✅ VERIFICAR AUTENTICAÇÃO ANTES DE TENTAR INICIALIZAR
            const isAuthenticated = this.authService.authStateSignal().isAuthenticated;
            if (!isAuthenticated) {
                return;
            }

            const currentPermission = Notification.permission;
            if (currentPermission === 'granted' &&
                this.pushNotificationService.isSupported() &&
                !this.pushNotificationInicializada) {
                // Tentar inicializar (o método já verifica se já existe subscription)
                this.inicializarPushNotificationsAutomaticamente();
            }
        }, 10000); // Verificar a cada 10 segundos
    }

    /**
     * Inicializa push notifications automaticamente se já tiver permissão
     * Não requer interação do usuário - executa silenciosamente
     * IMPORTANTE: Só inicializa se o usuário estiver autenticado
     */
    private inicializarPushNotificationsAutomaticamente(): void {
        // Evitar múltiplas tentativas
        if (this.pushNotificationInicializada) {
            return;
        }

        // ✅ VERIFICAR SE USUÁRIO ESTÁ AUTENTICADO ANTES DE INICIALIZAR
        const isAuthenticated = this.authService.authStateSignal().isAuthenticated;
        if (!isAuthenticated) {
            console.log('[AppComponent] ⏸️ Push notifications: aguardando autenticação do usuário');
            return;
        }

        // Verificar se push notifications são suportadas
        if (!this.pushNotificationService.isSupported()) {
            return;
        }

        const delayMs = this.mobilePlatform.isNative() ? 500 : 2000;

        const tentarInicializar = () => {
            if (this.pushNotificationInicializada) {
                return;
            }
            this.pushNotificationInicializada = true;

            setTimeout(() => {
                this.pushNotificationService
                    .inicializar()
                    .then((sucesso: boolean) => {
                        if (sucesso) {
                            console.log('[AppComponent] ✅ Push inicializado');
                        } else {
                            this.pushNotificationInicializada = false;
                        }
                    })
                    .catch(() => {
                        this.pushNotificationInicializada = false;
                    });
            }, delayMs);
        };

        if (this.mobilePlatform.isNative()) {
            tentarInicializar();
            return;
        }

        this.pushNotificationService.hasPermission().then((hasPermission: boolean) => {
            if (hasPermission) {
                tentarInicializar();
            }
        });
    }

    // Métodos privados para logging e cleanup
    private logPWAStatus(): void {
        // Status do PWA (silencioso)
    }

    private cleanupSubscriptions(): void {
        this.modalSub?.unsubscribe();
        this.feedbackSub?.unsubscribe();
        this.routerSub?.unsubscribe();
    }
}
